import { useEffect, useRef, useState } from 'react';
import { getCurrentSearchDates, getCurrentTabTimePeriod } from '../../common/helpers/utilities';
import { setStateData } from '../../common/helpers/state';
import { useCommonStore, useSearchStore } from '../../store/store';
import { getDefaultContactInfoColumns, getDefaultOverviewColumns, getMlsRestrictionSettings } from '../helpers/agentProductionHelpers';
import { getDefaultColumns } from '../helpers/listingProficiencyHelpers';
import { responseKeys } from '../../constants/service';
import {
    getDefaultGrowthAnalysisColumnsLtm,
    getDefaultGrowthAnalysisColumnsLtmIncDec,
    getDefaultGrowthAnalysisColumnsYtd,
    getDefaultGrowthAnalysisColumnsL90,
    getDefaultGrowthAnalysisColumnsYtdIncDec,
} from '../components/helpers/growthAnalysisHelpers';
import { modules } from '../../constants';
import { tabs } from '../constants/suggestedAgents';
import { views } from '../constants/savedAgents';

export const useSuggestedAgents = () => {
    const {
        timeIntervals,
        clearAgentListPopover,
    } = useCommonStore(state => ({
        timeIntervals: state.timeIntervals,
        clearAgentListPopover: state.clearAgentListPopover,
    }));
    const {
        currentTabId,
        selectedMls,
        selectedTimePeriod,
        setSuggestedAgentsData,
        setSummaryPinnedData,
        selectedListingViewId,
        moduleState,
    } = useSearchStore(state => {
            const moduleState = state[state.selectedModule];
            const currentTabId = moduleState?.currentTabId;
            const currentTabState = moduleState?.searchOptions?.[currentTabId];
            const selectedListingViewId = currentTabState?.display.selectedView.value;
            const isGrowthAnalysisView = selectedListingViewId === views.growthAnalysis;
            const selectedTimePeriod = getCurrentTabTimePeriod(currentTabState?.producers, isGrowthAnalysisView);
            return {
                currentTabId,
                selectedMls: currentTabState?.producers?.mls || moduleState?.selectedMls,
                selectedTimePeriod,
                setSuggestedAgentsData: state.setSuggestedAgentsData,
                setSummaryPinnedData: state.setSummaryPinnedData,
                selectedListingViewId,
                moduleState,
            };
    });

    const gridRef = useRef();
    const restrictedMlsSettings = getMlsRestrictionSettings(selectedMls?.mlsId);
    const initialState = {
        rowDataByTab: {
            [tabs.topProducers.id]: { totals: undefined, results: undefined },
            [tabs.newProducers.id]: { totals: undefined, results: undefined },
            [tabs.incProducers.id]: { totals: undefined, results: undefined },
            [tabs.decProducers.id]: { totals: undefined, results: undefined },
            [tabs.coopAgents.id]: { totals: undefined,results: undefined }
        },
        currentSearchDates: getCurrentSearchDates(timeIntervals, selectedTimePeriod),
        overviewColumns: getDefaultOverviewColumns(),
        newProducersOverviewColumns: getDefaultOverviewColumns(true),
        coopAgentsOverviewColumns: getDefaultOverviewColumns(false, false, true),
        incDecOverviewColumns: getDefaultOverviewColumns(false, true),
        contactInfoColumns: getDefaultContactInfoColumns(restrictedMlsSettings),
        newProducersContactInfoColumns: getDefaultContactInfoColumns(restrictedMlsSettings, true),
        listingProficiencyColumns: getDefaultColumns(),
        growthAnalysisColumnsLtm: getDefaultGrowthAnalysisColumnsLtm(),
        growthAnalysisColumnsYtd: getDefaultGrowthAnalysisColumnsYtd(),
        growthAnalysisColumnsL90: getDefaultGrowthAnalysisColumnsL90(),
        growthAnalysisColumnsLtmIncDec: getDefaultGrowthAnalysisColumnsLtmIncDec(),
        growthAnalysisColumnsYtdIncDec: getDefaultGrowthAnalysisColumnsYtdIncDec(),
        selectedFilterValues: {},
        propertyTypeNames: [],
        validationSettings: {},
    };
    const [state, setState] = useState(initialState);

    const currentTabData = state.rowDataByTab[currentTabId];

    useEffect(() => {
        setStateData('currentSearchDates', getCurrentSearchDates(timeIntervals, selectedTimePeriod), setState);
    }, [timeIntervals, selectedTimePeriod]);

    useEffect(() => {
        clearAgentListPopover();
        if (!selectedMls?.mlsId || moduleState.id !== modules.proficiencyMetrics.suggestedAgents) return;
        setStateData('currentSearchDates', getCurrentSearchDates(timeIntervals, selectedTimePeriod), setState);
        if (currentTabData?.results !== undefined) return;
        setSuggestedAgentsData({
            mlsId: selectedMls?.mlsId,
            setState,
            currentTabId,
        });
    }, [selectedMls?.mlsId, currentTabId, timeIntervals]);

    useEffect(() => {
        if (!selectedMls?.mlsId) return;
        setSummaryPinnedData(selectedListingViewId, responseKeys.recruitingSoldTotals, currentTabData?.totals);
        setState(state => ({
            ...state,
            contactInfoColumns: getDefaultContactInfoColumns(restrictedMlsSettings),
        }))
    }, [selectedListingViewId, selectedMls?.mlsId, currentTabId, currentTabData]);

    useEffect(() => setState(initialState), [selectedMls?.mlsId]);

    return { gridRef, state, setState };
};
